import React, { createRef, Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  BypassBlock,
  classNames,
  Helmet,
  ToastMode,
  ToastProvider,
  useDefineMessages,
  useToasts,
  TooltipProvider,
} from '@groma-dev/blueprint';
import { initializeAuthInterceptor, SiteExp } from '@groma-dev/scaffold';
import { Route, Routes, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import { AppConfig } from '../../../core/AppConfig';
import { jumpToAnchorRef, scrollToTop } from '../../utils/WebHelper';
import { useAppStore, useDataStore } from '../../providers/StoresProvider';
import { ComponentFactory } from '../../ComponentFactory';
import { AppRoutes } from '../../../core/routes';
import { APIClient } from '../../../core/apiclient/APIClient';
import AuthRouter from '../authrouter/AuthRouter';
import OnboardingRouter from '../onboardingrouter/OnboardingRouter';
import { AppMode } from '../../stores/AppStoreInterface';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';
import { LocalStorage } from '../../../core/utils/Constants';
import { getAppStyles } from './styles/App.styles';

const AccreditationFlow = React.lazy(() => ComponentFactory.AccreditationFlowAsync);
const AccreditedInvestor = React.lazy(() => ComponentFactory.AccreditedInvestorAsync);
const AccountSelect = React.lazy(() => ComponentFactory.AccountSelectAsync);
const AddEntityContactInfo = React.lazy(() => ComponentFactory.AddEntityContactInfoAsync);
const AddEntityDetails = React.lazy(() => ComponentFactory.AddEntityDetailsAsync);
const ClaimVerifyIdentity = React.lazy(() => ComponentFactory.ClaimVerifyIdentityAsync);
const CreateInvestmentAccount = React.lazy(() => ComponentFactory.CreateInvestmentAccountAsync);
const Documents = React.lazy(() => ComponentFactory.DocumentsAsync);
const ErrorPage = React.lazy(() => ComponentFactory.ErrorPageAsync);
const GetStarted = React.lazy(() => ComponentFactory.GetStartedAsync);
const Help = React.lazy(() => ComponentFactory.HelpAsync);
const Home = React.lazy(() => ComponentFactory.HomeAsync);
const Invest = React.lazy(() => ComponentFactory.InvestAsync);
const InvestAmount = React.lazy(() => ComponentFactory.InvestAmountAsync);
const InvestConfirmation = React.lazy(() => ComponentFactory.InvestConfirmationAsync);
const InvestDocuments = React.lazy(() => ComponentFactory.InvestDocumentsAsync);
const InvestPayment = React.lazy(() => ComponentFactory.InvestPaymentAsync);
const InvestReview = React.lazy(() => ComponentFactory.InvestReviewAsync);
const InvestVerifyId = React.lazy(() => ComponentFactory.InvestVerifyIdAsync);
const Overview = React.lazy(() => ComponentFactory.OverviewAsync);
const SelectEntityType = React.lazy(() => ComponentFactory.SelectEntityTypeAsync);
const Settings = React.lazy(() => ComponentFactory.SettingsAsync);
const SideNav = React.lazy(() => ComponentFactory.SideNavAsync);
const VerifyEmail = React.lazy(() => ComponentFactory.VerifyEmailAsync);
const VerifyIdentity = React.lazy(() => ComponentFactory.VerifyIdentityAsync);
const VerifyInformation = React.lazy(() => ComponentFactory.VerifyInformationAsync);
const Wallet = React.lazy(() => ComponentFactory.WalletAsync);
const WelcomeComplete = React.lazy(() => ComponentFactory.WelcomeCompleteAsync);

const GromaCoinWelcome = React.lazy(() => ComponentFactory.GromaCoinWelcomeAsync);
const GromaCoinTransaction = React.lazy(() => ComponentFactory.GromaCoinTransactionAsync);
const GromaCoinNextSteps = React.lazy(() => ComponentFactory.GromaCoinNextStepsAsync);
const GromaCoinVerify = React.lazy(() => ComponentFactory.GromaCoinVerifyAsync);
const GromaCoinWaitlist = React.lazy(() => ComponentFactory.GromaCoinWaitlistAsync);

let authRedirectInitialized = false;

export interface AppProps {}

const GromaCoinRoutes = observer(() => {
  const { Seo } = AppConfig.Settings;

  return (
    <>
      {Seo.enabled && (
        <Helmet>
          <title>GromaCoin Claim</title>
        </Helmet>
      )}
      <Suspense fallback={<ComponentFactory.Loading isPage />}>
        <Routes>
          <Route index element={<GromaCoinWelcome />} />
          <Route path="claim/:claimId" element={<GromaCoinWelcome />} />
          <Route path="transaction" element={<GromaCoinTransaction />} />
          <Route path="next-steps" element={<GromaCoinNextSteps />} />
          <Route path="verify" element={<GromaCoinVerify />} />
          <Route path="waitlist" element={<GromaCoinWaitlist />} />
        </Routes>
      </Suspense>
    </>
  );
});

const AppContent = observer(() => {
  const dataStore = useDataStore();
  const appStore = useAppStore();
  const messages = useDefineMessages();
  const { mode } = appStore.state;
  const { formatMessage } = useIntl();
  const styles = getAppStyles();
  const { addToast } = useToasts();
  const { userState, accountsState } = dataStore;
  const { pathname } = useLocation();

  const fetchAccount = async (id: string) => {
    try {
      const response = await dataStore.fetchAccount({ id });

      if (response.success) {
        dataStore.setAccount(response.account);
      } else {
        setTimeout(
          () => addToast(response.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
      }
    } catch (e) {
      handleErrorLogging(e as Error);
    }
  };

  const fetchAccounts = async (page = 1) => {
    const { pageSize } = AppConfig.Components.App.accounts;
    try {
      const response = await dataStore.fetchAccounts({ page_size: pageSize, page });

      if (response.success) {
        dataStore.setAccounts([...(dataStore.accountsState.accounts ?? []), ...response.accounts]);
        if (response.nextPageUrl) {
          await fetchAccounts(page + 1);
        } else {
          const { accounts = [] } = dataStore.accountsState;

          if (accounts?.length > 0) {
            const lsInvestmentAccount = dataStore.getLocalStorage(LocalStorage.InvestmentAccount);
            const validAccountStored =
              lsInvestmentAccount && accounts.some(acc => acc.id === parseInt(lsInvestmentAccount));
            await fetchAccount(validAccountStored ? lsInvestmentAccount : `${accounts[0].id}`);
          }

          dataStore.setFetchingAccounts(false);
          dataStore.setAppInitialized(true);
        }
      } else if (response.status === 404) {
        dataStore.setAccounts([]);
        dataStore.setAppInitialized(true);
      } else {
        setTimeout(
          () => addToast(response.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
      }
    } catch (e) {
      handleErrorLogging(e as Error);
    }
  };

  useEffect(() => {
    if (userState.user) {
      if (!accountsState.isFetching && !accountsState.accounts) {
        fetchAccounts();
      }
    }
  }, [userState.user]);

  if (!dataStore.appState.initialized) return <ComponentFactory.Loading isPage />;

  return (
    <>
      <ComponentFactory.Header />
      <div id="mainContent" className={styles.mainContent}>
        <ComponentFactory.ErrorBoundary>
          <Suspense fallback={<ComponentFactory.Loading isPage />}>
            {mode === AppMode.Desktop && pathname !== AppRoutes.root && <SideNav />}
            {mode !== AppMode.Desktop && pathname !== AppRoutes.root && (
              <div className={classNames(styles.mobileAccountSelectContainer)}>
                <AccountSelect />
              </div>
            )}
            <Routes>
              <Route path={AppRoutes.root} element={<Home />} />
              <Route path={AppRoutes.Accreditation} element={<AccreditationFlow />} />
              <Route path={AppRoutes.Overview} element={<Overview />} />
              <Route path={AppRoutes.Wallet} element={<Wallet />} />
              <Route path={AppRoutes.Documents} element={<Documents />} />
              <Route path={AppRoutes.Settings} element={<Settings />} />
              <Route path={AppRoutes.Invest} element={<Invest />} />
              <Route path={AppRoutes.InvestAmount} element={<InvestAmount />} />
              <Route path={AppRoutes.InvestVerifyId} element={<InvestVerifyId />} />
              <Route path={AppRoutes.InvestDocuments} element={<InvestDocuments />} />
              <Route path={AppRoutes.InvestPayment} element={<InvestPayment />} />
              <Route path={AppRoutes.InvestReview} element={<InvestReview />} />
              <Route path={AppRoutes.InvestConfirmation} element={<InvestConfirmation />} />
              <Route path={AppRoutes.Help} element={<Help />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </Suspense>
        </ComponentFactory.ErrorBoundary>
      </div>
      <ComponentFactory.Footer />
    </>
  );
});

const App = observer(() => {
  const styles = getAppStyles();
  const { pathname, search, hash } = useLocation();
  const { Domains, Authentication } = AppConfig.Settings;
  const { name: authCookieName, domain } = Authentication.cookie;
  const resetRef = createRef<HTMLSpanElement>();

  const isGromaCoinRoute = pathname.startsWith('/gromacoin');

  if (!isGromaCoinRoute && Authentication.enabled && Cookies.get(authCookieName) && !authRedirectInitialized) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);
    initializeAuthInterceptor(
      APIClient,
      `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.INVESTORS}`,
      {
        name: authCookieName,
        domain,
      }
    );
    authRedirectInitialized = true;
  }

  useEffect(() => {
    if (hash) jumpToAnchorRef();
    else {
      scrollToTop();
      if (!hash) resetRef.current?.focus();
    }
  }, [pathname]);

  return (
    <>
      <span ref={resetRef} tabIndex={-1} />
      <BypassBlock id="mainContent">
        <FormattedMessage
          id="common.bypassBlocks.mainContent"
          defaultMessage="Skip to Main Content"
          description="Main bypass block content"
        />
      </BypassBlock>
      <ToastProvider>
        <div className={classNames(styles.mainContainer)}>
          <Routes>
            <Route
              path="/gromacoin/*"
              element={
                <ComponentFactory.ErrorBoundary>
                  <GromaCoinRoutes />
                </ComponentFactory.ErrorBoundary>
              }
            />
            <Route
              path="*"
              element={
                <ComponentFactory.ErrorBoundary>
                  <TooltipProvider>
                    <Routes>
                      <Route path={AppRoutes.Auth} element={<ComponentFactory.Auth />} />
                      <Route
                        path={AppRoutes.OnboardingGetStarted}
                        element={
                          <OnboardingRouter>
                            <GetStarted />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.Onboarding}
                        element={
                          <OnboardingRouter>
                            <GetStarted />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingVerifyEmail}
                        element={
                          <OnboardingRouter>
                            <VerifyEmail />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingCreateAccount}
                        element={
                          <OnboardingRouter>
                            <CreateInvestmentAccount />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingSelectEntityType}
                        element={
                          <OnboardingRouter>
                            <SelectEntityType />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingVerifyIdentity}
                        element={
                          <OnboardingRouter>
                            <VerifyIdentity />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingAddEntityDetails}
                        element={
                          <OnboardingRouter>
                            <AddEntityDetails />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingAddEntityContactInfo}
                        element={
                          <OnboardingRouter>
                            <AddEntityContactInfo />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingVerifyInformation}
                        element={
                          <OnboardingRouter>
                            <VerifyInformation />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingAccreditedInvestor}
                        element={
                          <OnboardingRouter>
                            <AccreditedInvestor />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingWelcomeComplete}
                        element={
                          <OnboardingRouter>
                            <WelcomeComplete />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingClaimVerifyIdentity}
                        element={
                          <Suspense fallback={<ComponentFactory.Loading isPage />}>
                            <ClaimVerifyIdentity />
                          </Suspense>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <AuthRouter>
                            <AppContent />
                          </AuthRouter>
                        }
                      />
                    </Routes>
                  </TooltipProvider>
                </ComponentFactory.ErrorBoundary>
              }
            />
          </Routes>
        </div>
      </ToastProvider>
    </>
  );
});

export default App;
